import { Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/hooks/useNotify';
import { useDetachWorkOrder, useWorkOrderConsolidation } from '@/hooks/useConsolidatedInvoices';
import { formatDate } from '@/utils/locale';
import { consolidationErrorText } from './consolidationError';

interface WorkOrderConsolidationNoticeProps {
  workOrderId: string;
  canManage: boolean;
  canRead: boolean;
}

/**
 * Active consolidated-invoice link on the work order Payments card (#3933).
 * "Laskuta erikseen" is only offered while the invoice is still collecting or
 * a draft; a sent invoice is corrected with a credit note.
 */
export function WorkOrderConsolidationNotice({
  workOrderId,
  canManage,
  canRead,
}: WorkOrderConsolidationNoticeProps) {
  const { t } = useTranslation('workorders');
  const { t: ti } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const consolidation = useWorkOrderConsolidation(workOrderId, canRead);
  const detach = useDetachWorkOrder();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const link = canRead ? consolidation.data?.link ?? null : null;
  if (!link) return null;

  const numberLabel = link.invoiceNumber ?? t('consolidation.openInvoice');
  const start = formatDate(link.periodStart);
  const end = formatDate(link.periodEnd);
  const canDetach = canManage && (link.invoiceStatus === 'collecting' || link.invoiceStatus === 'draft');

  const handleDetach = async () => {
    try {
      await detach.mutateAsync({ workOrderId });
      notify.success(t('consolidation.detachSuccess'));
      setConfirmOpen(false);
    } catch (error) {
      notify.error(tc('error'), consolidationErrorText(error, ti));
    }
  };

  return (
    <>
      <Stack
        spacing={1}
        sx={{ mb: 2 }}
        data-testid="work-order-consolidation"
        data-invoice-status={link.invoiceStatus}
        data-invoice-number={link.invoiceNumber ?? ''}
        data-number-label={numberLabel}
        data-period-start={link.periodStart ?? ''}
        data-period-end={link.periodEnd ?? ''}
      >
        <Typography variant="body2">
          {t('consolidation.added', { number: numberLabel, start, end })}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            component={RouterLink}
            to={`/invoices/${link.invoiceId}`}
            size="small"
            variant="outlined"
            data-testid="work-order-consolidation-link"
          >
            {ti('actions.openInvoice')}
          </Button>
          {canDetach ? (
            <Button
              size="small"
              variant="outlined"
              data-testid="consolidation-detach"
              onClick={() => setConfirmOpen(true)}
            >
              {t('consolidation.billSeparately')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
      <ConfirmDialog
        open={confirmOpen}
        title={t('consolidation.billSeparatelyTitle')}
        message={t('consolidation.billSeparatelyConfirm')}
        confirmText={t('consolidation.billSeparately')}
        onConfirm={() => { void handleDetach(); }}
        onCancel={() => {
          if (!detach.isPending) setConfirmOpen(false);
        }}
        loading={detach.isPending}
      />
    </>
  );
}
